<script setup lang="ts">
import { lectureService } from '@/api/services/lecture-service'
import BaseVideoThumnail from '@/components/base/BaseVideoThumbnail.vue'
import { useIsMobile } from '@/composables/useMediaQuery'
import type { ApiCursorBody } from '@/types/api-response.types'
import type { Lecture } from '@/types/lecture.types'
import type { OpenClassFilter } from '@/types/open-class.types'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useRouter } from 'vue-router'

const isMobile = useIsMobile()
const router = useRouter()

// props로 현재 선택된 필터 받기
const props = defineProps<{
  filters: OpenClassFilter
}>()

// 스크롤 감지를 위한 변수들
const isLoading = ref(false)
const observer = ref<IntersectionObserver | null>(null)
const loadMoreTrigger = ref<HTMLElement | null>(null)

// 무료 클래스 데이터
const lectureCursorInfo = ref<ApiCursorBody<Lecture>>()
const freeClassList = ref<Lecture[]>([])

const ascending = computed(() => {
  return props.filters.sort.data === 'latest' ? false : true
})

const level = computed(() => {
  if (typeof props.filters.level.data !== 'string') return null
  if (props.filters.level.data === 'all') return null

  return props.filters.level.data.toUpperCase()
})

const hoveredItemId = ref<number | null>(null)

const handleMouseEnter = (itemId: number) => {
  if (isMobile.value) return
  hoveredItemId.value = itemId
}

const handleMouseLeave = () => {
  if (isMobile.value) return
  hoveredItemId.value = null
}

const handleItemClick = (item: Lecture) => {
  router.push(`/video/${item.idx}`)
}

// 추가 강의 로드 함수
const loadMoreLectures = async () => {
  if (isLoading.value || !lectureCursorInfo.value?.hasNext) return

  isLoading.value = true
  try {
    const nextCursor = lectureCursorInfo.value?.nextCursor
    const lectureCursorList = await lectureService.getLectures({
      free: true,
      cursor: nextCursor,
      size: 18,
      level: level.value,
      ascending: ascending.value,
    })

    freeClassList.value = [...freeClassList.value, ...lectureCursorList.data]
    lectureCursorInfo.value = lectureCursorList
  } catch (error) {
    console.error('강의 로드 중 오류 발생:', error)
  } finally {
    isLoading.value = false
  }
}

// Intersection Observer 설정
const setupIntersectionObserver = () => {
  observer.value = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        loadMoreLectures()
      }
    },
    { threshold: 0.1 },
  )

  if (loadMoreTrigger.value) {
    observer.value.observe(loadMoreTrigger.value)
  }
}

watch(props.filters, async () => {
  freeClassList.value = []
  const lectureCursorList = await lectureService.getLectures({
    free: true,
    cursor: undefined,
    size: 18,
    level: level.value,
    ascending: ascending.value,
  })
  freeClassList.value = lectureCursorList.data
  lectureCursorInfo.value = lectureCursorList
})

onMounted(async () => {
  const lectureCursorList = await lectureService.getLectures({
    free: true,
    cursor: undefined,
    size: 18,
    level: level.value,
    ascending: ascending.value,
  })
  freeClassList.value = lectureCursorList.data
  lectureCursorInfo.value = lectureCursorList

  setupIntersectionObserver()
})

onUnmounted(() => {
  if (observer.value) {
    observer.value.disconnect()
  }
})
</script>

<template>
  <div class="free-class-list">
    <BaseVideoThumnail
      class="list-item"
      v-for="item in freeClassList"
      :key="item.idx"
      :lecture="item"
      :show-level="isMobile || hoveredItemId === item.idx"
      :is-free="true"
      @mouseenter="handleMouseEnter(item.idx)"
      @mouseleave="handleMouseLeave"
      @video-click="handleItemClick(item)"
    />

    <!-- 결과가 없는 경우 -->
    <div v-if="freeClassList.length === 0" class="empty-state">
      <p class="body-md gray-600">해당 조건에 맞는 결과가 없습니다.</p>
    </div>

    <!-- 로딩 트리거 요소 -->
    <div ref="loadMoreTrigger" class="load-more-trigger">
      <div v-if="isLoading" class="loading-spinner">로딩 중...</div>
    </div>
  </div>
</template>

<style scoped>
.free-class-list {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 20px;
  margin-top: 28px;
}

.list-item:hover {
  transform: scale(1.24);
  z-index: 100;
  border: 1px solid var(--tint-300);
  cursor: pointer;
}

.free-class-list:has(.list-item:hover) .list-item:not(:hover) {
  opacity: 0.2;
  transition: opacity 0.1s ease;
}

.empty-state {
  grid-column: 1 / -1;
  text-align: center;
  padding: 40px 0;
}

.load-more-trigger {
  grid-column: 1 / -1;
  height: 50px;
  display: flex;
  justify-content: center;
  align-items: center;
}

.loading-spinner {
  color: var(--gray-600);
}

@media (max-width: 1365px) {
  .free-class-list {
    grid-template-columns: 1fr;
    gap: 16px;
    margin-top: 0px;
  }

  .list-item:hover {
    transform: none;
    border: none;
  }

  .free-class-list:has(.list-item:hover) .list-item:not(:hover) {
    opacity: 1;
    transition: none;
  }
}
</style>
